import React from 'react';
import { cancelVideoCall } from 'flow/actions/video2-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import { getContactAvatar } from 'flow/utils';
import { addVideoLogEntry } from 'flow/utils/browser-utils';

interface Props {
    videoCallState: any;
    callStatus?: string | null;
    onStatusChange?: (status: string) => void;
    contact?: any;
    contactName?: string;
    contactNumber?: string;
    companyName?: string | null;
    statusText?: string | null;
}

const OutgoingVideoCall: React.FC<Props> = ({
    videoCallState,
    callStatus = null,
    onStatusChange,
    contact,
    contactName,
    contactNumber,
    companyName,
    statusText,
}) => {
    const { i18next } = useAppContext();

    const onCancel = async () => {
        try {
            const calleeExtensionKey = videoCallState?.get('extensionKey');

            if (calleeExtensionKey) {
                await cancelVideoCall(calleeExtensionKey);
                addVideoLogEntry(
                    `Canceled Video2 call to ${calleeExtensionKey}`,
                );
            }
        } catch (error) {
            console.error('Error canceling video call:', error);
        }

        onStatusChange?.('canceled');
    };

    return (
        <div className="no-dark animate-slideInDialpad panel:w-[360px] flex h-full w-screen min-w-[360px] shrink-0 flex-col items-center gap-1 overflow-hidden bg-black p-2">
            <div className="mt-9 flex w-full flex-col items-center justify-center rounded-sm bg-gray-800 text-gray-800">
                <div className="text-yellow my-2 font-bold">
                    {statusText || i18next.t('videocanvas.outgoing.video.call')}
                </div>
                <UserAvatar src={getContactAvatar(contact)} size="extraLarge" />
                <div className="mx-3 mt-3 flex flex-col items-center justify-center gap-1.5">
                    <div className="overflow-hidden text-center text-ellipsis whitespace-nowrap text-white">
                        {contactName}
                    </div>
                    {companyName ? (
                        <div className="text-sm/4 text-gray-400">
                            {companyName}
                        </div>
                    ) : null}

                    <div className="text-sm text-gray-400">{contactNumber}</div>
                </div>
                {!statusText && (
                    <div className="my-3 flex h-16 flex-auto items-stretch justify-center gap-5">
                        <div className="flex flex-col items-center gap-2">
                            <IconButton
                                aria-label={i18next.t(
                                    'Call.Canvas.Title.Cancel',
                                )}
                                onClick={onCancel}
                                className="bg-red hover:brighten-10 size-10 rounded-xl text-white"
                            >
                                <Icon iconKey="videocam_off" />
                            </IconButton>
                            <span className="text-xs text-white">
                                {i18next.t('Call.Canvas.Title.Cancel')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OutgoingVideoCall;
